import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";
import {
  Search, Plus, RefreshCw, Download, FileUp, FileDown, Trash2, Eye, EyeOff,
  Filter, FileSpreadsheet, Table, X, XIcon, ZoomIn, ZoomOut,
  MoreVertical, RotateCcw, Shield, ArrowUpDown, ArrowUp, ArrowDown,
  GripVertical, Paperclip, Mail, MessageSquare, Edit3, Settings2,
  Check, Calendar, List, Circle, Euro, Percent, Hash, Phone, Calculator,
  RefreshCcw, Undo2, Redo2, TrendingUp, TrendingDown, Minus, Copy, BadgeEuro,
  CheckSquare, ListChecks, Pencil, Image as ImageIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  TableSidebar, NotificationBell, InlineDatePicker, InlineDropdownPicker, CommentsPanel,
  DEFAULT_WIDTH, MIN_WIDTH,
  getCellValue, formatCellValue, updateAllKeyVariants, resolveActualKey,
} from "@/components/tableau";
import { evaluateFormula } from "@/components/tableau/utils/formulaEngine";
import { useFabricLibrary, swatchImageUrl, attachmentImageUrl } from "@/components/fabric/useFabricLibrary";
import { resolveOrderFabrics, MANUAL_FABRICS_FIELD } from "@/components/fabric/fabricUtils";
import { FabricCellEditor } from "@/components/fabric/FabricCellEditor";
import { useTableData } from "@/components/tableau/hooks/useTableData";
import { useFiltering } from "@/components/tableau/hooks/useFiltering";
import { useCellEditing } from "@/components/tableau/hooks/useCellEditing";
import { useColumnManagement } from "@/components/tableau/hooks/useColumnManagement";
import { useAttachments } from "@/components/tableau/hooks/useAttachments";
import { useRowOperations } from "@/components/tableau/hooks/useRowOperations";
import { useResize } from "@/components/tableau/hooks/useResize";
import { useCellSelection } from "@/components/tableau/hooks/useCellSelection";
import { useUndoRedo } from "@/components/tableau/hooks/useUndoRedo";
import { BackupManagerModal } from "@/components/tableau/BackupManagerModal";
import { CellPopupModal } from "@/components/tableau/modals/CellPopupModal";
import { ImportPreviewModal } from "@/components/tableau/modals/ImportPreviewModal";
import { ColumnTypeModal } from "@/components/tableau/modals/ColumnTypeModal";
import { AttachmentsModal, EmailsModal, AddColumnModal, StatusFilterDropdown, PreviewLightbox } from "@/components/tableau/modals/TableauModals";
import { EnrichOrderModal } from "@/components/tableau/modals/EnrichOrderModal";
import { DuplicatesModal } from "@/components/tableau/modals/DuplicatesModal";
import { RevenueFixModal } from "@/components/tableau/modals/RevenueFixModal";
import { ExportCsvModal } from "@/components/tableau/modals/ExportCsvModal";
import { ShipCompleteDialog } from "@/components/tableau/modals/ShipCompleteDialog";
import { BulkEditDialog } from "@/components/tableau/modals/BulkEditDialog";
import { ExtractionValidationModal } from "@/components/ExtractionValidationModal";
import TableRow from "@/components/tableau/TableRow";

// Column name migration helper
const COLUMN_NAME_MIGRATIONS = {
  "TYPE REGLT.": "PAYMENT METHOD",
  "TYPE_REGLT.": "PAYMENT METHOD",
  "type_reglt.": "PAYMENT METHOD",
};
const migrateColumnName = (name) => COLUMN_NAME_MIGRATIONS[name] || name;

export default function Tableau() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // ==================== HOOKS ====================
  const {
    orders, setOrders, columns, setColumns, columnOrder, setColumnOrder,
    isLoading, availableStatuses, availableTypes, attachmentCounts, setAttachmentCounts,
    emailCounts, setEmailCounts, commentCounts, setCommentCounts,
    fabricPhotos, setFabricPhotos,
    hasNewChanges, setHasNewChanges, changedBy, columnWidths, setColumnWidths,
    serverColumnTypes, zoom, setZoom,
    fetchData, refreshData, markRowAsModified,
    isRowHighlighted, markAllAsSeen, highlightedRowsCount, getOrderedColumns,
  } = useTableData(user, navigate);

  const onFabricPhotosChanged = useCallback((orderId, keys) => setFabricPhotos(prev => ({ ...prev, [orderId]: keys })), [setFabricPhotos]);

  const {
    globalSearch, setGlobalSearch, showAdvancedFilters, setShowAdvancedFilters,
    filterRules, setFilterRules, filterLogic, setFilterLogic,
    statusFilter, showStatusFilter, setShowStatusFilter,
    statusFilterPosition, setStatusFilterPosition,
    typeFilter, showTypeFilter, setShowTypeFilter,
    typeFilterPosition, setTypeFilterPosition,
    sortColumn, sortDirection,
    addFilterRule, updateFilterRule, removeFilterRule, clearAllFilters,
    toggleStatusFilter, clearStatusFilter, selectAllStatuses,
    toggleTypeFilter, clearTypeFilter, selectAllTypes,
    isAnyFilterActive, isStatusFilterActive, isTypeFilterActive,
    getOperatorsForColumn, sortedOrders, handleSort,
  } = useFiltering(orders, columns, getOrderedColumns, getCellValue, availableStatuses, availableTypes);

  const [shipPrompt, setShipPrompt] = useState({ open: false, order: null });
  const [preferredCarrier, setPreferredCarrier] = useState(() => {
    try { return localStorage.getItem('orderflow_default_carrier') || ''; } catch { return ''; }
  });

  const {
    selectedCell, setSelectedCell, editValue, setEditValue, isEditing, setIsEditing,
    showDatePicker, setShowDatePicker, datePickerPosition, datePickerContext, setDatePickerContext,
    showDropdownPicker, setShowDropdownPicker, dropdownPickerPosition, dropdownPickerContext, setDropdownPickerContext,
    dropdownOptions, setDropdownOptions, customDropdownValue, setCustomDropdownValue,
    inlineEdit, setInlineEdit, inlineInputRef,
    handleCellClick, handleInlineAmountSave, handleInlineAmountCancel,
    handleRestoreCalculatedValue, handleDateSelect, handleClearDate, parseDateValue,
    handleDropdownSelect, handleAddCustomDropdownOption,
    handleDeleteDropdownOption, handleClearDropdownValue,
    handleSaveCell, handleSaveCellWithValue, handleCopyContent, handlePrintContent,
  } = useCellEditing({
    orders, setOrders, columns, markRowAsModified,
    onStatusTerminated: (order) => setShipPrompt({ open: true, order }),
  });

  // Carrier choices for the shipping dialog: existing values (by frequency) + curated list
  const carrierOptions = useMemo(() => {
    const counts = new Map();
    orders.forEach((o) => {
      const ed = o.extracted_data || {};
      const v = ed[resolveActualKey(ed, 'TRANS. COMPANY')];
      if (v !== undefined && v !== null && String(v).trim() !== '') {
        const s = String(v).trim();
        counts.set(s, (counts.get(s) || 0) + 1);
      }
    });
    const existing = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]).map((e) => e[0]);
    const curated = ['POSTE - COL.CHRONO.', 'Colissimo', 'Chronopost', 'DPD', 'Mondial Relay', 'GLS', 'UPS', 'DHL', 'Colis Privé', 'EN PROPRE'];
    const seen = new Set();
    const out = [];
    [preferredCarrier, ...existing, ...curated].forEach((c) => {
      if (!c) return;
      const low = c.toLowerCase();
      if (!seen.has(low)) { seen.add(low); out.push(c); }
    });
    return out;
  }, [orders, preferredCarrier]);

  // Auto-fill shipping columns when an order is marked TERMINE (only empty cells)
  const handleFillShipping = useCallback(async (carrier) => {
    const order = shipPrompt.order;
    setShipPrompt({ open: false, order: null });
    if (!order) return;
    const transValue = (carrier && String(carrier).trim()) ? String(carrier).trim() : 'POSTE - COL.CHRONO.';
    try { localStorage.setItem('orderflow_default_carrier', transValue); } catch { /* ignore */ }
    setPreferredCarrier(transValue);
    const d = new Date();
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const fills = [
      { name: 'SHIPPED ON', value: iso },
      { name: "AVIS D'EXPEDITION", value: iso },
      { name: 'TRANS. COMPANY', value: transValue },
    ];
    let updatedData = { ...(order.extracted_data || {}) };
    let changed = false;
    fills.forEach((f) => {
      const key = resolveActualKey(updatedData, f.name);
      const cur = updatedData[key];
      if (cur === undefined || cur === null || String(cur).trim() === '') {
        updatedData = updateAllKeyVariants(updatedData, f.name, f.value);
        changed = true;
      }
    });
    if (!changed) { toast.info("Colonnes d'expédition déjà renseignées"); return; }
    try {
      const res = await authFetch(`${API}/orders/${order.order_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_id: order.order_id, extracted_data: updatedData }),
      });
      if (res.ok) {
        setOrders(prev => prev.map(o => o.order_id === order.order_id ? { ...o, extracted_data: updatedData } : o));
        markRowAsModified(order.order_id);
        toast.success("Colonnes d'expédition remplies");
      } else { toast.error("Erreur lors du remplissage"); }
    } catch { toast.error("Erreur lors du remplissage"); }
  }, [shipPrompt, setOrders, markRowAsModified]);


  const {
    showColumnTypeModal, setShowColumnTypeModal, selectedColumnForType,
    columnTypeForm, setColumnTypeForm, existingValues,
    contextMenu, showAddColumnModal, setShowAddColumnModal,
    newColumnForm, setNewColumnForm, isCreatingColumn, formulaInputRef,
    COLUMN_TYPES_WITH_ICONS,
    handleColumnContextMenu, closeContextMenu, openColumnTypeModal,
    handleSaveColumnType, addDropdownOption, removeDropdownOption, addAllExistingValues,
    handleDeleteColumn, handleCreateColumn,
    getColumnType, getColumnDropdownOptions, getColumnStatusConfig, getStatusColor, renderStatusCell,
  } = useColumnManagement({ columns, setColumns, serverColumnTypes, orders, setOrders });

  const {
    attachmentModal, emailModal, attachments, emails, isUploading, emailForm,
    emailAttachments, detectedAttachments, previewModal, commentsModal,
    openAttachmentModal, closeAttachmentModal, handleFileUpload, handleDeleteAttachment,
    downloadAttachment, previewAttachment, closePreviewModal,
    openEmailModal, closeEmailModal, openCommentsModal, closeCommentsModal,
    handleEmailContentChange, handleEmailAttachmentSelect,
    removeEmailAttachment, handleAddEmail, handleDeleteEmail, handleTogglePrintWithSheet,
  } = useAttachments({ orders, setOrders, setAttachmentCounts, setEmailCounts, commentCounts, setCommentCounts, onFabricPhotosChanged });

  const {
    draggedRow, dragOverRow, draggedColumn, dragOverColumn,
    showImportModal, setShowImportModal, importPreview, setImportPreview, isImporting,
    fileInputRef,
    handleAddRow, handleDeleteRow,
    handleDragStart, handleDragOver, handleDragLeave, handleDrop, handleDragEnd,
    handleColumnDragStart, handleColumnDragOver, handleColumnDragLeave, handleColumnDrop, handleColumnDragEnd,
    resetColumnOrder, handleImportClick, handleFileSelect, handleConfirmImport,
  } = useRowOperations({ orders, setOrders, markRowAsModified, columnOrder, setColumnOrder, columns });

  const { resizing, handleResizeStart, resetColumnWidths } = useResize({ columnWidths, setColumnWidths });

  // Undo/Redo system
  const {
    pushAction: pushUndoAction, undo, redo,
    canUndo, canRedo, undoCount, redoCount,
  } = useUndoRedo({ orders, setOrders, markRowAsModified });

  // Memoized ordered columns (stable identity so TableRow memo works)
  const orderedColumns = useMemo(() => getOrderedColumns(), [getOrderedColumns]);

  // Fabric photo alerts shown in the CONTENT cell
  const fabricLibrary = useFabricLibrary();
  const contentColumn = useMemo(() => columns.find(c => String(c.name || '').toUpperCase() === 'CONTENT'), [columns]);
  // Per-row fabric info: CONTENT alert icon + FABRIC column thumbnails
  const fabricInfo = useMemo(() => {
    const info = {};
    if (!contentColumn) return info;
    orders.forEach(o => {
      const r = resolveOrderFabrics(getCellValue(o, contentColumn), fabricLibrary, fabricPhotos[o.order_id] || [], o.extracted_data?.[MANUAL_FABRICS_FIELD]);
      const items = [
        ...r.fabrics.map(f => ({ id: f.key, label: f.label, code: f.code, collection: f.collection, url: f.orderPhoto ? attachmentImageUrl(o.order_id, f.orderPhoto) : f.swatch ? swatchImageUrl(f.swatch) : null })),
        ...r.generalPhotos.map(p => ({ id: p.attachment_id, label: 'Photo jointe à la commande', code: '', collection: '', url: attachmentImageUrl(o.order_id, p) })),
      ];
      info[o.order_id] = { alert: r.alert, items, sig: `${r.alert || ''}#${items.map(i => `${i.id}@${i.url || ''}`).join('|')}` };
    });
    return info;
  }, [orders, contentColumn, fabricLibrary, fabricPhotos]);

  // Fabric codes typed by hand (CONTENT popup or FABRIC cell editor), stored in extracted_data._fabrics
  const [fabricEditor, setFabricEditor] = useState(null);
  const openFabricEditor = useCallback((orderId, anchor) => setFabricEditor({ orderId, anchor }), []);
  const handleSetManualFabrics = useCallback(async (orderId, keys) => {
    try {
      const res = await authFetch(`${API}/orders/${orderId}/fabrics`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ keys }),
      });
      if (!res.ok) { toast.error("Erreur lors de l'enregistrement de la toile"); return; }
      const { fabrics } = await res.json();
      setOrders(prev => prev.map(o => {
        if (o.order_id !== orderId) return o;
        const extracted_data = { ...(o.extracted_data || {}) };
        if (fabrics) extracted_data[MANUAL_FABRICS_FIELD] = fabrics;
        else delete extracted_data[MANUAL_FABRICS_FIELD];
        return { ...o, extracted_data };
      }));
      markRowAsModified(orderId);
    } catch { toast.error("Erreur réseau"); }
  }, [setOrders, markRowAsModified]);
  const fabricEditorOrder = fabricEditor ? orders.find(o => o.order_id === fabricEditor.orderId) : null;

  const {
    selectedCells, copiedCells, dragFillRange, isDragFilling, lastSelectedCell,
    handleCellSelect, clearSelection,
    handleDragFillStart, handleDragFillMove,
    isCellSelected, isCellCopied, isCellInDragFill,
  } = useCellSelection({
    orders, setOrders, columns: orderedColumns, markRowAsModified,
    filteredOrders: sortedOrders,
    pushUndoAction,
  });

  // ==================== LOCAL STATE ====================
  const [showBackupManager, setShowBackupManager] = useState(false);
  const [activeRowId, setActiveRowId] = useState(null);
  const [navigatedRowId, setNavigatedRowId] = useState(null);
  const navigatedTimerRef = useRef(null);
  const [notifRefreshTrigger, setNotifRefreshTrigger] = useState(0);
  const tableRef = useRef(null);
  const statusFilterBtnRef = useRef(null);
  const typeFilterBtnRef = useRef(null);

  // ==================== BULK ROW SELECTION / EDIT ====================
  const [selectedRowIds, setSelectedRowIds] = useState(() => new Set());
  const [bulkEditOpen, setBulkEditOpen] = useState(false);

  const toggleRowSelect = useCallback((orderId) => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId); else next.add(orderId);
      return next;
    });
  }, []);

  const clearRowSelection = useCallback(() => setSelectedRowIds(new Set()), []);

  const selectedFilteredCount = useMemo(
    () => sortedOrders.reduce((acc, o) => acc + (selectedRowIds.has(o.order_id) ? 1 : 0), 0),
    [sortedOrders, selectedRowIds]
  );
  const allFilteredSelected = sortedOrders.length > 0 && selectedFilteredCount === sortedOrders.length;

  const toggleSelectAllFiltered = useCallback(() => {
    setSelectedRowIds((prev) => {
      const ids = sortedOrders.map((o) => o.order_id);
      const everySelected = ids.length > 0 && ids.every((id) => prev.has(id));
      const next = new Set(prev);
      if (everySelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  }, [sortedOrders]);

  const handleBulkApply = useCallback(async (columnName, value) => {
    const colType = getColumnType(columnName);
    const updates = [];
    const localMap = new Map();
    orders.forEach((o) => {
      if (!selectedRowIds.has(o.order_id)) return;
      const ed = o.extracted_data || {};
      let updatedData;
      if (colType === 'checkbox') {
        const key = resolveActualKey(ed, columnName);
        updatedData = { ...ed, [key]: value === true };
      } else {
        updatedData = updateAllKeyVariants(ed, columnName, value);
      }
      updates.push({ order_id: o.order_id, extracted_data: updatedData });
      localMap.set(o.order_id, updatedData);
    });
    if (updates.length === 0) { setBulkEditOpen(false); return; }
    try {
      const res = await authFetch(`${API}/orders/bulk-update`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
      if (res.ok) {
        const data = await res.json();
        setOrders((prev) => prev.map((o) => localMap.has(o.order_id) ? { ...o, extracted_data: localMap.get(o.order_id) } : o));
        updates.forEach((u) => markRowAsModified(u.order_id));
        toast.success(`${data.modified_count ?? updates.length} ligne(s) mise(s) à jour`);
        setBulkEditOpen(false);
        clearRowSelection();
      } else { toast.error("Erreur lors de la modification groupée"); }
    } catch { toast.error("Erreur lors de la modification groupée"); }
  }, [orders, selectedRowIds, getColumnType, setOrders, markRowAsModified, clearRowSelection]);

  // ==================== ROW VIRTUALIZATION ====================
  const VIRTUAL_OVERSCAN = 10;
  const [rowHeight, setRowHeight] = useState(37);
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 80 });
  const visibleRangeRef = useRef(visibleRange);
  visibleRangeRef.current = visibleRange;

  useEffect(() => {
    const c = tableRef.current;
    if (!c) return;
    let raf = null;
    const update = () => {
      const start = Math.max(0, Math.floor(c.scrollTop / rowHeight) - VIRTUAL_OVERSCAN);
      const end = Math.min(sortedOrders.length, Math.ceil((c.scrollTop + c.clientHeight) / rowHeight) + VIRTUAL_OVERSCAN);
      const cur = visibleRangeRef.current;
      if (start !== cur.start || end !== cur.end) setVisibleRange({ start, end });
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = null; update(); });
    };
    update();
    c.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      c.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [sortedOrders.length, rowHeight, isLoading]);

  // Measure actual row height (varies with zoom)
  useEffect(() => {
    const c = tableRef.current;
    if (!c) return;
    const tr = c.querySelector('tbody tr[data-order-id]');
    if (tr && tr.offsetHeight > 0 && Math.abs(tr.offsetHeight - rowHeight) > 1) {
      setRowHeight(tr.offsetHeight);
    }
  }, [sortedOrders.length, zoom, rowHeight, isLoading, visibleRange]);

  // Enrichment state
  const [enrichModal, setEnrichModal] = useState({ open: false, orderId: null, orderName: "" });
  const [validationModal, setValidationModal] = useState({ open: false, mode: "enrichissement", extractedData: {}, existingData: {}, orderId: null });
  const [isSavingValidation, setIsSavingValidation] = useState(false);
  const [rowContextMenu, setRowContextMenu] = useState({ show: false, x: 0, y: 0, orderId: null, orderName: "" });

  const openEnrichModal = useCallback((orderId, orderName) => {
    setEnrichModal({ open: true, orderId, orderName });
    setRowContextMenu({ show: false, x: 0, y: 0, orderId: null, orderName: "" });
  }, []);

  // ==================== DUPLICATE DETECTION ====================
  const [showDuplicatesModal, setShowDuplicatesModal] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const openDuplicatesModal = useCallback(() => setShowDuplicatesModal(true), []);

  // ==================== REVENUE (TVA) CORRECTION ====================
  const [revenueFix, setRevenueFix] = useState({ open: false, scanning: false, applying: false, result: null });

  const handleScanRevenueErrors = async () => {
    setRevenueFix({ open: true, scanning: true, applying: false, result: null });
    try {
      const res = await authFetch(`${API}/orders/scan-revenue-errors`, { method: "POST" });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setRevenueFix({ open: true, scanning: false, applying: false, result: data });
    } catch {
      toast.error("Erreur lors de l'analyse des revenus");
      setRevenueFix({ open: false, scanning: false, applying: false, result: null });
    }
  };

  const handleApplyRevenueFix = async (orderIds) => {
    if (!orderIds || orderIds.length === 0) return;
    setRevenueFix((prev) => ({ ...prev, applying: true }));
    try {
      const res = await authFetch(`${API}/orders/fix-revenue-errors`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order_ids: orderIds }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      toast.success(`${data.count} revenu(s) corrigé(s)`);
      const ordersRes = await authFetch(`${API}/orders`);
      if (ordersRes.ok) {
        const od = await ordersRes.json();
        setOrders(od.orders || []);
      }
      setRevenueFix({ open: false, scanning: false, applying: false, result: null });
    } catch {
      toast.error("Erreur lors de la correction des revenus");
      setRevenueFix((prev) => ({ ...prev, applying: false }));
    }
  };

  const duplicateGroups = useMemo(() => {
    // Use the SAME key resolution as the table display (getCellValue) to avoid
    // false positives from stale key variants (e.g. leftover NAME vs name)
    const nameCol = columns.find(c => c.name === "NAME") || { name: "NAME" };
    const dateCol = columns.find(c => c.name === "DATE OF ORDER") || { name: "DATE OF ORDER", type: "date" };
    const map = new Map();
    for (const o of orders) {
      const name = String(getCellValue(o, nameCol) || "").trim().toUpperCase().replace(/\s+/g, " ");
      const date = String(getCellValue(o, dateCol) || "").trim();
      if (!name || !date) continue;
      const key = `${name}|${date}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(o);
    }
    return [...map.entries()]
      .filter(([, arr]) => arr.length > 1)
      .map(([key, arr]) => ({
        key,
        name: key.split("|")[0],
        date: key.split("|")[1],
        orders: arr,
      }));
  }, [orders, columns]);

  const duplicateOrderIds = useMemo(() => {
    const s = new Set();
    duplicateGroups.forEach(g => g.orders.forEach(o => s.add(o.order_id)));
    return s;
  }, [duplicateGroups]);

  const handleRecalcMargins = async () => {
    try {
      // Passe 1 : backend (toutes les commandes, tolérant aux variantes de clés)
      const res = await authFetch(`${API}/orders/recalculate-margins`, { method: "POST" });
      const stats = res.ok ? await res.json() : { updated: 0, skipped_existing: 0 };

      // Passe 2 : frontend — exactement la même résolution que l'affichage (y compris colonnes calculées)
      const displayValue = (o, colName, fallbackType) => {
        const col = columns.find(c => c.name === colName) || { name: colName, type: fallbackType };
        const colType = getColumnType(colName);
        if (colType === 'calcul' && col.formula) {
          const overrideValue = o.extracted_data?.[`_override_${colName}`];
          if (overrideValue !== undefined && overrideValue !== null && overrideValue !== '') return overrideValue;
          return evaluateFormula(col.formula, o.extracted_data || {}, columns);
        }
        return getCellValue(o, col);
      };
      const parseMoney = (v) => {
        if (v === null || v === undefined) return null;
        let s = String(v).replace(/[^\d.,-]/g, "");
        if (!s) return null;
        if (s.includes(",") && s.includes(".")) {
          s = s.lastIndexOf(",") > s.lastIndexOf(".") ? s.replace(/\./g, "").replace(",", ".") : s.replace(/,/g, "");
        } else {
          s = s.replace(",", ".");
        }
        const n = parseFloat(s);
        return isNaN(n) ? null : n;
      };
      const writeField = (ed, colName, value) => {
        // Calcul columns display the formula result: store as manual override (read first by the table)
        if (getColumnType(colName) === 'calcul') {
          return { ...ed, [`_override_${colName}`]: value };
        }
        return updateAllKeyVariants(ed, colName, value);
      };
      let frontUpdated = 0;
      for (const o of orders) {
        if (parseMoney(displayValue(o, "MARGIN WITHOUT TAX", "amount")) !== null) continue;
        const length = parseMoney(displayValue(o, "LENGTH (ml)", "numerique"));
        const revenue = parseMoney(displayValue(o, "REVENUE TAX INCLUDED", "amount"));
        if (!length || !revenue || length <= 0 || revenue <= 0) continue;
        const purchase = Math.round(length * 5.64 * 100) / 100;
        const revenueHT = Math.round((revenue / 1.2) * 100) / 100;
        const margin = Math.round((revenueHT - purchase) * 100) / 100;
        const pct = purchase > 0 ? Math.round((margin / purchase) * 10000) / 100 : 0;
        let ed = o.extracted_data || {};
        ed = writeField(ed, "MARGIN WITHOUT TAX", String(margin));
        ed = writeField(ed, "MARGIN %", String(pct));
        ed = writeField(ed, "REVENUE WITHOUT TAX", String(revenueHT));
        if (parseMoney(displayValue(o, "PURCHASE PRICE", "amount")) === null) {
          ed = writeField(ed, "PURCHASE PRICE", String(purchase));
        }
        const putRes = await authFetch(`${API}/orders/${o.order_id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ order_id: o.order_id, extracted_data: ed }),
        });
        if (putRes.ok) frontUpdated++;
      }

      const totalUpdated = (stats.updated || 0) + frontUpdated;
      if (totalUpdated > 0) {
        toast.success(`Marges recalculées sur ${totalUpdated} commande(s)`);
        const ordersRes = await authFetch(`${API}/orders`);
        if (ordersRes.ok) {
          const data = await ordersRes.json();
          setOrders(data.orders || []);
        }
      } else {
        const parts = [`${stats.skipped_existing} déjà remplies`];
        if (stats.skipped_no_length) parts.push(`${stats.skipped_no_length} sans métrage`);
        if (stats.skipped_no_revenue) parts.push(`${stats.skipped_no_revenue} sans revenue`);
        toast.info(`Aucune marge à recalculer (${parts.join(", ")})`);
      }
    } catch {
      toast.error("Erreur lors du recalcul des marges");
    }
  };

  const handleMergeDuplicates = async (keepOrderId, mergeOrderIds) => {    try {
      const res = await authFetch(`${API}/orders/merge`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ keep_order_id: keepOrderId, merge_order_ids: mergeOrderIds }),
      });
      if (res.ok) {
        const data = await res.json();
        setOrders(prev => prev.filter(o => !mergeOrderIds.includes(o.order_id)).map(o => o.order_id === keepOrderId ? data.order : o));
        toast.success(`${data.merged_count} commande(s) fusionnée(s)`);
      } else {
        toast.error("Erreur lors de la fusion");
      }
    } catch {
      toast.error("Erreur lors de la fusion");
    }
  };

  const handleDeleteDuplicate = async (orderId) => {
    try {
      const res = await authFetch(`${API}/orders/${orderId}`, { method: "DELETE" });
      if (res.ok) {
        setOrders(prev => prev.filter(o => o.order_id !== orderId));
        toast.success("Commande supprimée");
      } else {
        toast.error("Erreur lors de la suppression");
      }
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Deselect active row when clicking outside the table
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (tableRef.current && !tableRef.current.contains(e.target)) {
        setActiveRowId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleEnrichExtracted = (newExtractedData, emailContent) => {
    const order = orders.find(o => o.order_id === enrichModal.orderId);
    const existingData = order?.extracted_data || {};
    setEnrichModal({ open: false, orderId: null, orderName: "" });
    setValidationModal({
      open: true,
      mode: "enrichissement",
      extractedData: newExtractedData,
      existingData: existingData,
      orderId: enrichModal.orderId,
      emailContent: emailContent,
    });
  };

  const handleValidationSave = async (validatedData, existingOrderId) => {
    const orderId = existingOrderId || validationModal.orderId;
    if (!orderId) { toast.error("Erreur: aucune commande à mettre à jour"); return; }
    setIsSavingValidation(true);
    try {
      const updateResponse = await authFetch(`${API}/orders/${orderId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order_id: orderId, extracted_data: validatedData }),
      });
      if (updateResponse.ok) {
        // Save the email as an enrichment email
        if (validationModal.emailContent) {
          await authFetch(`${API}/orders/${orderId}/emails`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ type: "enrichissement", content: validationModal.emailContent, date: new Date().toISOString() }),
          });
        }
        // Update local state
        setOrders(prev => prev.map(o => o.order_id === orderId ? { ...o, extracted_data: validatedData } : o));
        markRowAsModified(orderId);
        toast.success("Ligne mise à jour avec succès !");
      } else {
        const error = await updateResponse.json();
        toast.error(error.detail || "Erreur lors de la mise à jour");
      }
    } catch (error) {
      console.error("Update failed:", error);
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setIsSavingValidation(false);
      setValidationModal({ open: false, mode: "enrichissement", extractedData: {}, existingData: {}, orderId: null });
    }
  };

  const navigateToRow = (orderId) => {
    // Clear any previous timer
    if (navigatedTimerRef.current) clearTimeout(navigatedTimerRef.current);
    // Set the highlighted row
    setNavigatedRowId(orderId);
    // Scroll via computed offset (the row may not be in the DOM with virtualization)
    setTimeout(() => {
      const c = tableRef.current;
      const idx = sortedOrders.findIndex(o => o.order_id === orderId);
      if (c && idx >= 0) {
        c.scrollTo({ top: Math.max(0, idx * rowHeight - c.clientHeight / 2), behavior: "smooth" });
      } else {
        const el = document.querySelector(`[data-order-id="${orderId}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
    // Clear highlight after same duration as modifications (1 hour)
    navigatedTimerRef.current = setTimeout(() => setNavigatedRowId(null), 60 * 60 * 1000);
  };

  const handleRowContextMenu = useCallback((e, orderId, orderName) => {
    e.preventDefault();
    setRowContextMenu({ show: true, x: e.clientX, y: e.clientY, orderId, orderName });
  }, []);

  // ==================== TOOLBAR ACTIONS ====================
  const handleLogout = async () => {
    try { await authFetch(`${API}/auth/logout`, { method: 'POST' }); } catch {}
    logout();
    navigate('/');
  };

  const handleExportAll = async () => {
    try {
      toast.info("Export en cours...");
      const response = await authFetch(`${API}/orders/export/xlsx`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `export_tableau_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success("Export réussi !");
      } else { toast.error("Erreur lors de l'export"); }
    } catch { toast.error("Erreur lors de l'export"); }
  };

  // Export the currently FILTERED + sorted rows to CSV (client-side, WYSIWYG - matches the table)
  const handleExportCsvFiltered = (selectedColumnNames = null, rawNumbers = false) => {
    if (sortedOrders.length === 0) { toast.error("Aucune ligne à exporter"); return; }
    const exportCols = orderedColumns.filter((c) => !selectedColumnNames || selectedColumnNames.includes(c.name));
    if (exportCols.length === 0) { toast.error("Sélectionnez au moins une colonne"); return; }

    const displayValue = (order, col) => {
      const type = getColumnType(col.name);
      if (type === 'fabric') return (fabricInfo[order.order_id]?.items || []).map(i => i.label).join(', ');
      const ed = order.extracted_data || {};
      if (type === 'calcul' && col.formula) {
        const ov = ed[`_override_${col.name}`];
        const raw = (ov !== undefined && ov !== null && ov !== '') ? ov : evaluateFormula(col.formula, ed, columns);
        if (raw === null || raw === undefined || raw === '') return '';
        const decimals = col.calcul_decimal_places ?? 2;
        if (col.calcul_format === 'percentage') return `${Number(raw).toLocaleString('fr-FR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })} %`;
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(Number(raw));
      }
      const raw = getCellValue(order, col);
      if (type === 'checkbox') return formatCellValue(raw, 'checkbox', col) ? 'Oui' : 'Non';
      const formatted = formatCellValue(raw, type, col);
      return (formatted === null || formatted === undefined) ? '' : String(formatted);
    };

    // Convert a displayed value to a pure number string (French comma decimal), or null if not numeric
    const toRawNumber = (v) => {
      const s = String(v).trim();
      if (!s) return null;
      const cleaned = s.replace(/[€%\u00a0\s]/g, '');
      if (!/^-?[\d.,]+$/.test(cleaned)) return null; // contains letters/symbols -> keep as text
      let norm;
      if (cleaned.includes('.') && cleaned.includes(',')) {
        norm = cleaned.lastIndexOf(',') > cleaned.lastIndexOf('.') ? cleaned.replace(/\./g, '').replace(',', '.') : cleaned.replace(/,/g, '');
      } else if (cleaned.includes(',')) {
        norm = cleaned.replace(',', '.');
      } else {
        norm = cleaned;
      }
      const n = parseFloat(norm);
      if (isNaN(n)) return null;
      return String(n).replace('.', ',');
    };

    const cellValue = (order, col) => {
      const dv = displayValue(order, col);
      if (rawNumbers) { const n = toRawNumber(dv); if (n !== null) return n; }
      return dv;
    };

    const esc = (v) => {
      const s = v == null ? '' : String(v);
      return /[";\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const header = exportCols.map((c) => esc(c.name)).join(';');
    const rows = sortedOrders.map((o) => exportCols.map((c) => esc(cellValue(o, c))).join(';'));
    const csv = '\ufeff' + [header, ...rows].join('\r\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `export_filtre_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    setExportModalOpen(false);
    toast.success(`${sortedOrders.length} ligne(s) exportée(s) en CSV`);
  };

  // Quick date-range presets (this month / last month / this year) applied to the DATE OF ORDER column
  const applyDatePreset = (preset) => {
    const dateCol = columns.find((c) => c.name === 'DATE OF ORDER') || columns.find((c) => (c.type || getColumnType(c.name)) === 'date');
    if (!dateCol) { toast.error("Aucune colonne de date trouvée"); return; }
    const now = new Date();
    let from, to;
    if (preset === 'this_month') { from = new Date(now.getFullYear(), now.getMonth(), 1); to = new Date(now.getFullYear(), now.getMonth() + 1, 0); }
    else if (preset === 'last_month') { from = new Date(now.getFullYear(), now.getMonth() - 1, 1); to = new Date(now.getFullYear(), now.getMonth(), 0); }
    else { from = new Date(now.getFullYear(), 0, 1); to = new Date(now.getFullYear(), 11, 31); }
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    setFilterRules([{ id: Date.now(), column: dateCol.name, operator: 'date_between', value: iso(from), value2: iso(to) }]);
    setFilterLogic('and');
    setShowAdvancedFilters(true);
    const label = preset === 'this_month' ? 'Ce mois' : preset === 'last_month' ? 'Mois dernier' : 'Cette année';
    toast.success(`Filtre « ${label} » appliqué`);
  };



  const handleClearAll = async () => {
    if (!window.confirm("⚠ Vider tout le tableau ?\n\nCette action supprimera toutes les commandes. Cette action est irréversible.")) return;
    try {
      const response = await authFetch(`${API}/orders/clear-all`, { method: "DELETE" });
      if (response.ok) { setOrders([]); toast.success("Tableau vidé"); }
      else { toast.error("Erreur lors du vidage"); }
    } catch { toast.error("Erreur lors du vidage"); }
  };

  // ==================== COMPUTED ====================
  const fontSize = Math.max(8, Math.min(16, zoom / 10 + 2));
  const totalTableWidth = 90 + orderedColumns.reduce((sum, col) => sum + (columnWidths[col.name] || DEFAULT_WIDTH), 0);

  // Virtualization: only render the visible window of rows
  const virtualStart = Math.min(visibleRange.start, sortedOrders.length);
  const virtualEnd = Math.min(Math.max(visibleRange.end, virtualStart), sortedOrders.length);
  const visibleOrders = sortedOrders.slice(virtualStart, virtualEnd);
  const virtualTopPad = virtualStart * rowHeight;
  const virtualBottomPad = (sortedOrders.length - virtualEnd) * rowHeight;

  const getSortIcon = (columnName) => {
    if (sortColumn !== columnName) return <ArrowUpDown className="w-3 h-3 opacity-40" />;
    return sortDirection === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />;
  };

  // "A FABRIQUER USINE" — trend persisted in localStorage, reset every hour
  const currentFabCount = useMemo(() => {
    return orders.filter(o => {
      const ed = o.extracted_data || {};
      const status = String(ed["order_status"] || ed["ORDER STATUS"] || ed["order.status"] || ed["ORDER.STATUS"] || ed["status"] || "").trim().toUpperCase();
      return status.startsWith("A FABRIQUER USINE");
    }).length;
  }, [orders]);

  const [referenceFabCount, setReferenceFabCount] = useState(() => {
    try {
      const stored = localStorage.getItem("fab_usine_ref");
      if (stored) {
        const { count, timestamp } = JSON.parse(stored);
        const ageMs = Date.now() - timestamp;
        // Keep reference for 1 hour max
        if (ageMs < 60 * 60 * 1000) return count;
      }
    } catch {}
    return null;
  });

  // Set reference when first real count arrives and no valid stored reference exists
  useEffect(() => {
    if (currentFabCount > 0 && referenceFabCount === null) {
      setReferenceFabCount(currentFabCount);
      localStorage.setItem("fab_usine_ref", JSON.stringify({ count: currentFabCount, timestamp: Date.now() }));
    }
  }, [currentFabCount, referenceFabCount]);

  // Reset reference every hour
  useEffect(() => {
    const interval = setInterval(() => {
      setReferenceFabCount(currentFabCount);
      localStorage.setItem("fab_usine_ref", JSON.stringify({ count: currentFabCount, timestamp: Date.now() }));
    }, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [currentFabCount]);

  // ==================== RENDER ====================
  return (
    <div className="h-screen bg-zinc-50 flex overflow-hidden">
      {/* Sidebar */}
      <TableSidebar user={user} onLogout={handleLogout} />

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="flex-shrink-0 bg-white border-b border-zinc-200 px-4 md:px-6 py-3">
          <div className="flex items-center gap-3">
            {/* Left: Title */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <h1 className="text-lg md:text-xl font-bold text-zinc-900 font-['Manrope'] flex items-center gap-2 whitespace-nowrap">
                <FileSpreadsheet className="w-5 h-5 md:w-6 md:h-6 text-orange-500" />
                Tableau
              </h1>
              <span className="text-xs md:text-sm text-zinc-500 whitespace-nowrap">
                {sortedOrders.length}/{orders.length} ligne{orders.length > 1 ? "s" : ""}
              </span>
              <span className="text-xs md:text-sm text-orange-600 font-medium whitespace-nowrap flex items-center gap-1" data-testid="fab-usine-count">
                A fabriquer usine : {currentFabCount}
                {referenceFabCount !== null ? (
                  currentFabCount > referenceFabCount ? (
                    <TrendingUp className="w-4 h-4 text-red-500" />
                  ) : currentFabCount < referenceFabCount ? (
                    <TrendingDown className="w-4 h-4 text-green-500" />
                  ) : (
                    <Minus className="w-4 h-4 text-orange-400" />
                  )
                ) : (
                  <Minus className="w-4 h-4 text-orange-400" />
                )}
              </span>
              {duplicateGroups.length > 0 && (
                <button
                  onClick={() => setShowDuplicatesModal(true)}
                  className="text-xs bg-amber-100 text-amber-700 hover:bg-amber-200 px-2 py-1 rounded flex items-center gap-1 whitespace-nowrap transition-colors"
                  title="Voir les commandes en double"
                  data-testid="duplicates-header-badge"
                >
                  <Copy className="w-3 h-3" />
                  Doublons : {duplicateGroups.length}
                </button>
              )}
              {(globalSearch || filterRules.length > 0) && (
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded flex items-center gap-1 whitespace-nowrap">
                  <Filter className="w-3 h-3" />
                  Filtré
                </span>
              )}
              {sortColumn && (
                <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded whitespace-nowrap">
                  Trié: {sortColumn} {sortDirection === "asc" ? "↑" : "↓"}
                </span>
              )}
            </div>
            
            {/* Middle: Scrollable toolbar */}
            <div className="flex items-center gap-2 md:gap-3 overflow-x-auto scrollbar-thin flex-1 min-w-0">
              {/* Search bar */}
              <div className="relative flex-shrink-0">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input
                  value={globalSearch}
                  onChange={(e) => setGlobalSearch(e.target.value)}
                  placeholder="Rechercher..."
                  className="pl-9 w-36 md:w-48 h-8 md:h-9 text-sm"
                />
                {globalSearch && (
                  <button 
                    onClick={() => setGlobalSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                  >
                    <XIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
              
              {/* Advanced filters button */}
              <Button
                variant={filterRules.length > 0 ? "default" : "outline"}
                size="sm"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className={`gap-2 flex-shrink-0 ${filterRules.length > 0 ? 'bg-blue-500 hover:bg-blue-600' : ''}`}
              >
                <Filter className="w-4 h-4" />
                Filtres {filterRules.length > 0 && `(${filterRules.length})`}
              </Button>

              {/* Zoom controls */}
              <div className="hidden md:flex items-center gap-2 bg-zinc-100 rounded-lg px-3 py-1.5 flex-shrink-0">
                <ZoomOut className="w-4 h-4 text-zinc-500" />
                <Slider
                  value={[zoom]}
                  onValueChange={(v) => setZoom(v[0])}
                  min={50}
                  max={150}
                  step={10}
                  className="w-24"
                />
                <ZoomIn className="w-4 h-4 text-zinc-500" />
                <span className="text-xs text-zinc-600 w-10">{zoom}%</span>
              </div>
              
              {/* Undo/Redo buttons */}
              <div className="flex items-center gap-1 bg-zinc-100 rounded-lg px-1.5 py-1 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={undo}
                  disabled={!canUndo}
                  className="gap-1 h-7 px-2 text-zinc-700 hover:text-zinc-900 disabled:opacity-30"
                  title={canUndo ? `Annuler (${undoCount})` : "Rien à annuler"}
                  data-testid="undo-btn"
                >
                  <Undo2 className="w-4 h-4" />
                  {canUndo && <span className="text-xs font-medium tabular-nums">{undoCount}</span>}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={redo}
                  disabled={!canRedo}
                  className="gap-1 h-7 px-2 text-zinc-700 hover:text-zinc-900 disabled:opacity-30"
                  title={canRedo ? `Rétablir (${redoCount})` : "Rien à rétablir"}
                  data-testid="redo-btn"
                >
                  <Redo2 className="w-4 h-4" />
                  {canRedo && <span className="text-xs font-medium tabular-nums">{redoCount}</span>}
                </Button>
              </div>

              <Button
                size="sm"
                onClick={handleAddRow}
                data-testid="add-row-btn"
                className="gap-2 bg-orange-500 hover:bg-orange-600 text-white flex-shrink-0"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden md:inline">Ajouter ligne</span>
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={fetchData}
                data-testid="refresh-btn"
                className="gap-2 flex-shrink-0"
              >
                <RefreshCw className="w-4 h-4" />
                <span className="hidden md:inline">Actualiser</span>
              </Button>
              
              {highlightedRowsCount > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-100 rounded-lg border border-yellow-300 flex-shrink-0">
                  <span className="text-sm font-medium text-yellow-800 whitespace-nowrap" data-testid="modified-count">
                    {highlightedRowsCount} modif. récente{highlightedRowsCount > 1 ? 's' : ''}
                  </span>
                  <button
                    onClick={markAllAsSeen}
                    className="p-1 hover:bg-yellow-200 rounded transition-colors"
                    title="Tout marquer comme vu"
                    data-testid="mark-all-seen-btn"
                  >
                    <EyeOff className="w-4 h-4 text-yellow-700" />
                  </button>
                </div>
              )}
            </div>
            
            {/* Right: Fixed actions (outside overflow) */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Notification Bell */}
              <NotificationBell 
                orders={orders}
                refreshTrigger={notifRefreshTrigger}
                onNavigateToOrder={(orderId) => {
                  const order = orders.find(o => o.order_id === orderId);
                  if (order) {
                    const name = order.extracted_data?.NAME || order.data?.NAME || order.extracted_data?.client_name || `Commande`;
                    navigateToRow(orderId);
                    openCommentsModal(orderId, name);
                  }
                }}
              />
              
              {/* Menu déroulant pour Import/Export */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2">
                    <MoreVertical className="w-4 h-4" />
                    <span className="hidden md:inline">Plus</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={() => handleImportClick(true)} className="gap-2 cursor-pointer">
                    <Eye className="w-4 h-4" />
                    Importer avec aperçu
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleImportClick(false)} className="gap-2 cursor-pointer">
                    <FileUp className="w-4 h-4" />
                    Importer directement
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleExportAll} 
                    disabled={orders.length === 0}
                    className="gap-2 cursor-pointer"
                  >
                    <Download className="w-4 h-4" />
                    Exporter tout (.xlsx)
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setExportModalOpen(true)}
                    disabled={sortedOrders.length === 0}
                    className="gap-2 cursor-pointer"
                    data-testid="export-csv-filtered-btn"
                  >
                    <FileDown className="w-4 h-4" />
                    Exporter le filtre (.csv) {isAnyFilterActive ? `(${sortedOrders.length})` : ''}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={resetColumnOrder} className="gap-2 cursor-pointer">
                    <RotateCcw className="w-4 h-4" />
                    Réinitialiser ordre colonnes
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={resetColumnWidths} className="gap-2 cursor-pointer">
                    <RotateCcw className="w-4 h-4" />
                    Réinitialiser largeur colonnes
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setShowBackupManager(true)} className="gap-2 cursor-pointer" data-testid="backup-manager-btn">
                    <Shield className="w-4 h-4" />
                    Sauvegardes et restauration
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleRecalcMargins} className="gap-2 cursor-pointer" data-testid="recalc-margins-btn">
                    <Calculator className="w-4 h-4" />
                    Recalculer les marges manquantes
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleScanRevenueErrors} className="gap-2 cursor-pointer" data-testid="scan-revenue-errors-btn">
                    <BadgeEuro className="w-4 h-4" />
                    Corriger les revenus (TVA)
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleClearAll}
                    disabled={orders.length === 0}
                    className="gap-2 cursor-pointer text-red-600 focus:text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                    Vider le tableau
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              {/* Hidden file input for import */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".xlsx"
                className="hidden"
              />
            </div>
          </div>
        </header>

        {/* Bulk selection action bar */}
        {selectedRowIds.size > 0 && (
          <div className="flex items-center gap-3 px-4 md:px-6 py-2 bg-orange-500 text-white flex-shrink-0 flex-wrap" data-testid="bulk-action-bar">
            <span className="text-sm font-semibold flex items-center gap-2">
              <CheckSquare className="w-4 h-4" />
              <span data-testid="bulk-selected-count">{selectedRowIds.size}</span> ligne{selectedRowIds.size > 1 ? "s" : ""} sélectionnée{selectedRowIds.size > 1 ? "s" : ""}
            </span>
            {!allFilteredSelected && sortedOrders.length > selectedFilteredCount && (
              <button
                onClick={toggleSelectAllFiltered}
                className="text-xs bg-white/15 hover:bg-white/25 px-2.5 py-1 rounded transition-colors"
                data-testid="bulk-select-all-filtered"
              >
                Sélectionner tout le résultat filtré ({sortedOrders.length})
              </button>
            )}
            <Button
              size="sm"
              onClick={() => setBulkEditOpen(true)}
              className="h-7 bg-white text-orange-600 hover:bg-orange-50 gap-1.5"
              data-testid="bulk-edit-open-btn"
            >
              <Pencil className="w-3.5 h-3.5" /> Modifier une colonne
            </Button>
            <button
              onClick={clearRowSelection}
              className="text-xs text-white/90 hover:text-white underline ml-auto"
              data-testid="bulk-clear-selection"
            >
              Désélectionner
            </button>
          </div>
        )}

        {/* Active filters chips bar */}
        {(statusFilter.length > 0 || typeFilter.length > 0) && (
          <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border-b border-amber-200 flex-wrap" data-testid="active-filters-bar">
            <span className="text-xs font-medium text-amber-700 flex-shrink-0">Filtres actifs :</span>
            {statusFilter.map(s => (
              <span key={`status-${s}`} className="inline-flex items-center gap-1 px-2 py-0.5 bg-white border border-amber-300 rounded-full text-xs text-amber-800">
                <span className="font-medium">Statut:</span> {s}
                <button onClick={() => toggleStatusFilter(s)} className="ml-0.5 hover:text-red-600 transition-colors"><XIcon className="w-3 h-3" /></button>
              </span>
            ))}
            {typeFilter.map(t => (
              <span key={`type-${t}`} className="inline-flex items-center gap-1 px-2 py-0.5 bg-white border border-amber-300 rounded-full text-xs text-amber-800">
                <span className="font-medium">Type:</span> {t}
                <button onClick={() => toggleTypeFilter(t)} className="ml-0.5 hover:text-red-600 transition-colors"><XIcon className="w-3 h-3" /></button>
              </span>
            ))}
            <button onClick={clearAllFilters} className="text-xs text-red-600 hover:text-red-800 font-medium ml-1 flex-shrink-0">Effacer tout</button>
          </div>
        )}

        {/* Advanced Filters Panel */}
        {showAdvancedFilters && (
          <div className="flex-shrink-0 bg-zinc-50 border-b border-zinc-200 px-6 py-4">
            {/* Status Filter Section */}
            {availableStatuses.length > 0 && (
              <div className="mb-4 pb-4 border-b border-zinc-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-zinc-700 flex items-center gap-2">
                    <Filter className="w-4 h-4" />
                    Filtre ORDER STATUS
                    {isStatusFilterActive && (
                      <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                        {statusFilter.length} sélectionné{statusFilter.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </span>
                  <div className="flex items-center gap-2">
                    <button onClick={selectAllStatuses} className="text-xs px-2 py-1 bg-white border border-zinc-200 hover:bg-zinc-100 rounded">
                      Tout sélectionner
                    </button>
                    <button onClick={clearStatusFilter} className="text-xs px-2 py-1 bg-white border border-zinc-200 hover:bg-zinc-100 rounded" disabled={statusFilter.length === 0}>
                      Réinitialiser
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {availableStatuses.map((status) => (
                    <label 
                      key={status}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer transition-colors ${
                        statusFilter.includes(status) 
                          ? 'bg-orange-100 border-orange-300 text-orange-800' 
                          : 'bg-white border-zinc-200 hover:bg-zinc-50'
                      }`}
                    >
                      <Checkbox checked={statusFilter.includes(status)} onCheckedChange={() => toggleStatusFilter(status)} />
                      <span className="text-sm">{status}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            
            {/* Type Filter Section */}
            {availableTypes.length > 0 && (
              <div className="mb-4 pb-4 border-b border-zinc-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-zinc-700 flex items-center gap-2">
                    <Filter className="w-4 h-4" />
                    Filtre TYPE
                    {isTypeFilterActive && (
                      <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                        {typeFilter.length} sélectionné{typeFilter.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </span>
                  <div className="flex items-center gap-2">
                    <button onClick={selectAllTypes} className="text-xs px-2 py-1 bg-white border border-zinc-200 hover:bg-zinc-100 rounded">
                      Tout sélectionner
                    </button>
                    <button onClick={clearTypeFilter} className="text-xs px-2 py-1 bg-white border border-zinc-200 hover:bg-zinc-100 rounded" disabled={typeFilter.length === 0}>
                      Réinitialiser
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {availableTypes.map((type) => (
                    <label 
                      key={type}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer transition-colors ${
                        typeFilter.includes(type) 
                          ? 'bg-orange-100 border-orange-300 text-orange-800' 
                          : 'bg-white border-zinc-200 hover:bg-zinc-50'
                      }`}
                    >
                      <Checkbox checked={typeFilter.includes(type)} onCheckedChange={() => toggleTypeFilter(type)} />
                      <span className="text-sm">{type}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Advanced Filter Rules Section */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-zinc-700">Filtres avancés</span>
                <div className="flex items-center gap-2 bg-white rounded-lg border border-zinc-200 px-2 py-1">
                  <button onClick={() => setFilterLogic('and')} className={`px-2 py-1 text-xs rounded ${filterLogic === 'and' ? 'bg-orange-500 text-white' : 'text-zinc-600 hover:bg-zinc-100'}`}>ET</button>
                  <button onClick={() => setFilterLogic('or')} className={`px-2 py-1 text-xs rounded ${filterLogic === 'or' ? 'bg-orange-500 text-white' : 'text-zinc-600 hover:bg-zinc-100'}`}>OU</button>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-zinc-500">Période :</span>
                  <button onClick={() => applyDatePreset('this_month')} className="px-2 py-1 text-xs rounded border border-zinc-200 bg-white text-zinc-600 hover:bg-orange-50 hover:text-orange-700 hover:border-orange-300 transition-colors" data-testid="preset-this-month">Ce mois</button>
                  <button onClick={() => applyDatePreset('last_month')} className="px-2 py-1 text-xs rounded border border-zinc-200 bg-white text-zinc-600 hover:bg-orange-50 hover:text-orange-700 hover:border-orange-300 transition-colors" data-testid="preset-last-month">Mois dernier</button>
                  <button onClick={() => applyDatePreset('this_year')} className="px-2 py-1 text-xs rounded border border-zinc-200 bg-white text-zinc-600 hover:bg-orange-50 hover:text-orange-700 hover:border-orange-300 transition-colors" data-testid="preset-this-year">Cette année</button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={addFilterRule} className="gap-1">
                  <Plus className="w-4 h-4" />
                  Ajouter un filtre
                </Button>
                {filterRules.length > 0 && (
                  <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                    Effacer tout
                  </Button>
                )}
              </div>
            </div>
            
            {filterRules.length === 0 ? (
              <p className="text-sm text-zinc-500 italic">Aucun filtre actif. Cliquez sur "Ajouter un filtre" pour commencer.</p>
            ) : (
              <div className="space-y-2">
                {filterRules.map((rule, idx) => (
                  <div key={rule.id} className="flex items-center gap-2 bg-white rounded-lg border border-zinc-200 p-2">
                    {idx > 0 && <span className="text-xs text-zinc-500 w-8 text-center">{filterLogic === 'and' ? 'ET' : 'OU'}</span>}
                    {idx === 0 && <span className="w-8" />}
                    <Select value={rule.column} onValueChange={(val) => { updateFilterRule(rule.id, 'column', val); const ops = getOperatorsForColumn(val); if (ops.length > 0 && !ops.find(o => o.value === rule.operator)) updateFilterRule(rule.id, 'operator', ops[0].value); }}>
                      <SelectTrigger className="w-40 h-8"><SelectValue placeholder="Colonne" /></SelectTrigger>
                      <SelectContent>{orderedColumns.map((col) => <SelectItem key={col.name} value={col.name}>{col.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={rule.operator} onValueChange={(val) => updateFilterRule(rule.id, 'operator', val)}>
                      <SelectTrigger className="w-40 h-8"><SelectValue placeholder="Opérateur" /></SelectTrigger>
                      <SelectContent>{getOperatorsForColumn(rule.column).map((op) => <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>)}</SelectContent>
                    </Select>
                    {!['is_empty', 'is_not_empty'].includes(rule.operator) && (
                      rule.operator.includes('date') ? (
                        <div className="flex items-center gap-1 flex-1">
                          <Input type="date" value={rule.value || ''} onChange={(e) => updateFilterRule(rule.id, 'value', e.target.value)} className="h-8 flex-1" data-testid={`filter-value-${idx}`} />
                          {rule.operator === 'date_between' && (
                            <>
                              <span className="text-xs text-zinc-500 px-0.5">au</span>
                              <Input type="date" value={rule.value2 || ''} onChange={(e) => updateFilterRule(rule.id, 'value2', e.target.value)} className="h-8 flex-1" data-testid={`filter-value2-${idx}`} />
                            </>
                          )}
                        </div>
                      ) : rule.operator === 'between' ? (
                        <div className="flex items-center gap-1 flex-1">
                          <Input value={rule.value || ''} onChange={(e) => updateFilterRule(rule.id, 'value', e.target.value)} placeholder="Min..." className="h-8 flex-1" data-testid={`filter-value-${idx}`} />
                          <span className="text-xs text-zinc-500 px-0.5">et</span>
                          <Input value={rule.value2 || ''} onChange={(e) => updateFilterRule(rule.id, 'value2', e.target.value)} placeholder="Max..." className="h-8 flex-1" data-testid={`filter-value2-${idx}`} />
                        </div>
                      ) : (
                        <Input value={rule.value} onChange={(e) => updateFilterRule(rule.id, 'value', e.target.value)} placeholder="Valeur..." className="flex-1 h-8" data-testid={`filter-value-${idx}`} />
                      )
                    )}
                    <button onClick={() => removeFilterRule(rule.id)} className="p-1 hover:bg-red-100 rounded text-red-500">
                      <XIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Table Container */}
        <div 
          className={`flex-1 flex flex-col bg-white m-4 rounded-lg border overflow-hidden ${isAnyFilterActive ? 'border-orange-400 border-2' : 'border-zinc-200'}`}
          data-testid="table-container"
        >
          {/* Filter Alert Banner */}
          {isAnyFilterActive && (
            <div className="flex-shrink-0 bg-orange-50 border-b border-orange-200 px-4 py-2 flex items-center justify-between" data-testid="filter-alert-banner">
              <div className="flex items-center gap-3">
                <Filter className="w-4 h-4 text-orange-600" />
                <span className="text-sm font-medium text-orange-800">Filtres actifs :</span>
                <div className="flex items-center gap-2 flex-wrap">
                  {statusFilter.length > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                      Statut : {statusFilter.length === 1 ? statusFilter[0] : `${statusFilter.length} sélectionnés`}
                      <button onClick={clearStatusFilter} className="hover:bg-orange-200 rounded-full p-0.5"><XIcon className="w-3 h-3" /></button>
                    </span>
                  )}
                  {globalSearch && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                      Recherche : "{globalSearch}"
                      <button onClick={() => setGlobalSearch("")} className="hover:bg-blue-200 rounded-full p-0.5"><XIcon className="w-3 h-3" /></button>
                    </span>
                  )}
                  {filterRules.length > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                      {filterRules.length} filtre{filterRules.length > 1 ? 's' : ''} avancé{filterRules.length > 1 ? 's' : ''}
                      <button onClick={() => setFilterRules([])} className="hover:bg-purple-200 rounded-full p-0.5"><XIcon className="w-3 h-3" /></button>
                    </span>
                  )}
                </div>
                <span className="text-sm text-orange-600 ml-2">
                  → {sortedOrders.length} résultat{sortedOrders.length > 1 ? 's' : ''} sur {orders.length}
                </span>
              </div>
              <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-orange-700 hover:text-orange-900 hover:bg-orange-100 gap-1">
                <XIcon className="w-4 h-4" />
                Effacer tous les filtres
              </Button>
            </div>
          )}

          {/* Change notification banner */}
          {hasNewChanges && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between animate-pulse" data-testid="change-notification-banner">
              <div className="flex items-center gap-3">
                <RefreshCw className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-sm font-medium text-blue-800">Des modifications ont été effectuées</p>
                  <p className="text-xs text-blue-600" data-testid="change-notification-authors">Modifié par : {changedBy.join(", ")}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setHasNewChanges(false)} className="text-blue-600 hover:text-blue-800" data-testid="change-notification-ignore">Ignorer</Button>
                <Button size="sm" onClick={refreshData} className="bg-blue-600 hover:bg-blue-700 text-white gap-2" data-testid="refresh-data-btn">
                  <RefreshCw className="w-4 h-4" />
                  Actualiser
                </Button>
              </div>
            </div>
          )}
          
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            </div>
          ) : columns.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-zinc-500">
              <FileSpreadsheet className="w-12 h-12 mb-4 text-zinc-300" />
              <p>Aucun template de colonnes configuré</p>
              <p className="text-sm">Importez un fichier Excel dans Admin</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-zinc-500">
              <Table className="w-12 h-12 mb-4 text-zinc-300" />
              <p>Aucune commande enregistrée</p>
              <p className="text-sm">Extrayez des commandes depuis le Dashboard</p>
            </div>
          ) : (
            <>
              {/* Single Table with Sticky Header */}
              <div className="flex-1 overflow-auto" ref={tableRef}>
                <table 
                  className="border-separate border-spacing-0"
                  style={{ fontSize: `${fontSize}px`, tableLayout: 'fixed', width: `${totalTableWidth}px` }}
                  data-testid="orders-table"
                >
                  <thead className="sticky top-0 z-30">
                    <tr>
                      <th className="bg-orange-500 text-white font-semibold px-2 py-2 border-b border-r border-orange-600 whitespace-nowrap sticky left-0 z-40 relative" style={{ width: `${columnWidths['__row_number__'] || 160}px`, minWidth: '90px' }}>
                        <div className="flex items-center justify-center gap-2">
                          <input
                            type="checkbox"
                            checked={allFilteredSelected}
                            ref={(el) => { if (el) el.indeterminate = selectedFilteredCount > 0 && !allFilteredSelected; }}
                            onChange={toggleSelectAllFiltered}
                            className="w-3.5 h-3.5 accent-white cursor-pointer"
                            title="Tout sélectionner (résultat filtré)"
                            data-testid="select-all-rows-checkbox"
                          />
                          <span>#</span>
                        </div>
                        <div
                          className="absolute top-0 right-0 w-1 h-full bg-orange-700 hover:bg-white hover:w-1.5 cursor-col-resize transition-all"
                          onMouseDown={(e) => { e.stopPropagation(); handleResizeStart(e, '__row_number__'); }}
                          data-testid="resize-handle-row-number"
                        />
                      </th>
                      {orderedColumns.map((col, idx) => {
                        const isFirstColumn = idx === 0;
                        const isOrderStatusColumn = col.name.toLowerCase().includes('order') && col.name.toLowerCase().includes('status');
                        const isTypeColumn = col.name.toUpperCase() === 'TYPE';
                        const isFilterableColumn = isOrderStatusColumn || isTypeColumn;
                        return (
                          <th 
                            key={col.name}
                            className={`bg-orange-500 text-white font-semibold border-b border-r border-orange-600 relative ${draggedColumn === col.name ? 'opacity-50' : ''} ${dragOverColumn === col.name ? 'border-l-4 border-l-white' : ''} ${isFirstColumn ? 'sticky z-40' : ''} ${isOrderStatusColumn && isStatusFilterActive ? 'ring-2 ring-inset ring-yellow-300' : ''} ${isTypeColumn && isTypeFilterActive ? 'ring-2 ring-inset ring-yellow-300' : ''}`}
                            style={{ width: `${columnWidths[col.name] || DEFAULT_WIDTH}px`, minWidth: `${MIN_WIDTH}px`, ...(isFirstColumn ? { left: `${columnWidths['__row_number__'] || 160}px` } : {}) }}
                            data-testid={`column-header-${idx}`}
                            draggable={!isFirstColumn}
                            onDragStart={(e) => !isFirstColumn && handleColumnDragStart(e, col.name)}
                            onDragOver={(e) => handleColumnDragOver(e, col.name)}
                            onDragLeave={handleColumnDragLeave}
                            onDrop={(e) => handleColumnDrop(e, col.name)}
                            onDragEnd={handleColumnDragEnd}
                            onContextMenu={(e) => handleColumnContextMenu(e, col.name)}
                          >
                            <div 
                              className={`flex items-start justify-between px-3 py-2 min-h-[48px] ${isFirstColumn ? 'cursor-default' : 'cursor-grab'} hover:bg-orange-600 transition-colors select-none pr-4`}
                              onClick={() => !isFilterableColumn && getColumnType(col.name) !== 'fabric' && handleSort(col.name)}
                              data-testid={`sort-btn-${idx}`}
                            >
                              <div className="flex items-start gap-1 mr-2 flex-1">
                                {col.type && col.type !== 'text' && (
                                  <span className="opacity-70 flex-shrink-0 mt-0.5">
                                    {col.type === 'checkbox' && <Check className="w-3 h-3" />}
                                    {col.type === 'date' && <Calendar className="w-3 h-3" />}
                                    {col.type === 'dropdown' && <List className="w-3 h-3" />}
                                    {col.type === 'status' && <Circle className="w-3 h-3" />}
                                    {col.type === 'amount' && <Euro className="w-3 h-3" />}
                                    {col.type === 'percentage' && <Percent className="w-3 h-3" />}
                                    {col.type === 'numerique' && <Hash className="w-3 h-3" />}
                                    {col.type === 'phone' && <Phone className="w-3 h-3" />}
                                    {col.type === 'calcul' && <Calculator className="w-3 h-3" />}
                                    {col.type === 'fabric' && <ImageIcon className="w-3 h-3" />}
                                  </span>
                                )}
                                <span className="line-clamp-2 text-left leading-tight">{col.name}</span>
                                {isOrderStatusColumn && isStatusFilterActive && (
                                  <span className="ml-1 px-1.5 py-0.5 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full">{statusFilter.length}</span>
                                )}
                                {isTypeColumn && isTypeFilterActive && (
                                  <span className="ml-1 px-1.5 py-0.5 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full">{typeFilter.length}</span>
                                )}
                              </div>
                              {isOrderStatusColumn ? (
                                <div className="relative flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                  <button
                                    ref={statusFilterBtnRef}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      setStatusFilterPosition({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                                      setShowStatusFilter(!showStatusFilter);
                                    }}
                                    className={`p-1 rounded hover:bg-orange-400 transition-colors ${isStatusFilterActive ? 'bg-yellow-400 text-yellow-900' : ''}`}
                                    title="Filtrer par statut"
                                    data-testid="status-filter-btn"
                                  >
                                    <Filter className="w-4 h-4" />
                                  </button>
                                </div>
                              ) : isTypeColumn ? (
                                <div className="relative flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                  <button
                                    ref={typeFilterBtnRef}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      setTypeFilterPosition({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                                      setShowTypeFilter(!showTypeFilter);
                                    }}
                                    className={`p-1 rounded hover:bg-orange-400 transition-colors ${isTypeFilterActive ? 'bg-yellow-400 text-yellow-900' : ''}`}
                                    title="Filtrer par type"
                                    data-testid="type-filter-btn"
                                  >
                                    <Filter className="w-4 h-4" />
                                  </button>
                                </div>
                              ) : (
                                <span className="flex-shrink-0 mt-0.5">{getSortIcon(col.name)}</span>
                              )}
                            </div>
                            {/* Resize handle */}
                            <div
                              className="absolute top-0 right-0 w-1 h-full bg-orange-700 hover:bg-white hover:w-1.5 cursor-col-resize transition-all"
                              onMouseDown={(e) => { e.stopPropagation(); handleResizeStart(e, col.name); }}
                              data-testid={`resize-handle-${idx}`}
                              style={{ backgroundColor: resizing === col.name ? 'white' : undefined, width: resizing === col.name ? '4px' : undefined }}
                            />
                          </th>
                        );
                      })}
                      <th className="bg-orange-500 text-white font-semibold px-2 py-2 border-b border-r border-orange-600 cursor-pointer hover:bg-orange-600 transition-colors" style={{ width: '50px', minWidth: '50px' }} onClick={() => setShowAddColumnModal(true)} title="Ajouter une colonne" data-testid="add-column-btn">
                        <Plus className="w-5 h-5 mx-auto" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {virtualTopPad > 0 && <tr aria-hidden="true"><td colSpan={orderedColumns.length + 2} style={{ height: `${virtualTopPad}px`, padding: 0, border: 'none' }} /></tr>}
                    {visibleOrders.map((order, i) => {
                      const rowIdx = virtualStart + i;
                      const highlighted = isRowHighlighted(order.order_id);
                      const isActiveRow = activeRowId === order.order_id;
                      const isNavigated = navigatedRowId === order.order_id;
                      const isSAV = String(order.extracted_data?.["ORDER STATUS"] || order.extracted_data?.["order_status"] || "").toUpperCase().trim() === "SAV";
                      const baseBg = isNavigated ? "bg-yellow-200" : isSAV ? "bg-red-100" : isActiveRow ? "bg-green-100" : highlighted ? "bg-yellow-100" : (rowIdx % 2 === 0 ? "bg-white" : "bg-zinc-50");
                      return (
                        <TableRow
                          key={order.order_id}
                          order={order}
                          rowIdx={rowIdx}
                          orderedColumns={orderedColumns}
                          columnWidths={columnWidths}
                          DEFAULT_WIDTH={DEFAULT_WIDTH}
                          highlighted={highlighted}
                          isActiveRow={isActiveRow}
                          isNavigated={isNavigated}
                          isSAV={isSAV}
                          draggedRow={draggedRow}
                          dragOverRow={dragOverRow}
                          baseBg={baseBg}
                          attachmentCounts={attachmentCounts}
                          emailCounts={emailCounts}
                          commentCounts={commentCounts}
                          inlineEdit={inlineEdit}
                          inlineInputRef={inlineInputRef}
                          setInlineEdit={setInlineEdit}
                          handleCellClick={handleCellClick}
                          handleCellSelect={handleCellSelect}
                          handleInlineAmountSave={handleInlineAmountSave}
                          handleInlineAmountCancel={handleInlineAmountCancel}
                          handleDeleteRow={handleDeleteRow}
                          handleDragStart={handleDragStart}
                          handleDragEnd={handleDragEnd}
                          handleDragOver={handleDragOver}
                          handleDragLeave={handleDragLeave}
                          handleDrop={handleDrop}
                          openAttachmentModal={openAttachmentModal}
                          openEmailModal={openEmailModal}
                          openCommentsModal={openCommentsModal}
                          openEnrichModal={openEnrichModal}
                          handleRowContextMenu={handleRowContextMenu}
                          setActiveRowId={setActiveRowId}
                          getColumnType={getColumnType}
                          getCellValue={getCellValue}
                          formatCellValue={formatCellValue}
                          evaluateFormula={evaluateFormula}
                          columns={columns}
                          renderStatusCell={renderStatusCell}
                          isCellSelected={isCellSelected}
                          isCellCopied={isCellCopied}
                          isCellInDragFill={isCellInDragFill}
                          selectedCells={selectedCells}
                          copiedCells={copiedCells}
                          dragFillRange={dragFillRange}
                          lastSelectedCell={lastSelectedCell}
                          handleDragFillStart={handleDragFillStart}
                          isDragFilling={isDragFilling}
                          handleDragFillMove={handleDragFillMove}
                          handleRestoreCalculatedValue={handleRestoreCalculatedValue}
                          isDuplicate={duplicateOrderIds.has(order.order_id)}
                          openDuplicatesModal={openDuplicatesModal}
                          isRowSelected={selectedRowIds.has(order.order_id)}
                          onToggleRowSelect={toggleRowSelect}
                          fabricInfo={fabricInfo[order.order_id]}
                          onEditFabrics={openFabricEditor}
                        />
                      );
                    })}
                    {virtualBottomPad > 0 && <tr aria-hidden="true"><td colSpan={orderedColumns.length + 2} style={{ height: `${virtualBottomPad}px`, padding: 0, border: 'none' }} /></tr>}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </main>

      {/* Extracted Modal Components */}
      <CellPopupModal
        selectedCell={selectedCell} setSelectedCell={setSelectedCell} orders={orders}
        isEditing={isEditing} setIsEditing={setIsEditing} editValue={editValue} setEditValue={setEditValue}
        getColumnType={getColumnType} getColumnStatusConfig={getColumnStatusConfig} getStatusColor={getStatusColor}
        handleSaveCell={handleSaveCell} handleSaveCellWithValue={handleSaveCellWithValue}
        handleCopyContent={handleCopyContent} handlePrintContent={handlePrintContent}
        onFabricPhotosChanged={onFabricPhotosChanged}
        onSetManualFabrics={handleSetManualFabrics}
      />

      {fabricEditorOrder && (
        <FabricCellEditor
          orderId={fabricEditorOrder.order_id}
          name={fabricEditorOrder.extracted_data?.NAME || ''}
          content={contentColumn ? getCellValue(fabricEditorOrder, contentColumn) : ''}
          photos={fabricPhotos[fabricEditorOrder.order_id] || []}
          manual={fabricEditorOrder.extracted_data?.[MANUAL_FABRICS_FIELD] || ''}
          anchor={fabricEditor.anchor}
          onSetManualFabrics={handleSetManualFabrics}
          onClose={() => setFabricEditor(null)}
        />
      )}

      <ImportPreviewModal
        showImportModal={showImportModal} setShowImportModal={setShowImportModal}
        importPreview={importPreview} setImportPreview={setImportPreview}
        isImporting={isImporting} handleConfirmImport={handleConfirmImport}
      />

      <DuplicatesModal
        open={showDuplicatesModal}
        onClose={() => setShowDuplicatesModal(false)}
        groups={duplicateGroups}
        onMerge={handleMergeDuplicates}
        onDelete={handleDeleteDuplicate}
        onNavigate={(orderId) => { setShowDuplicatesModal(false); navigateToRow(orderId); }}
      />

      <RevenueFixModal
        open={revenueFix.open}
        onClose={() => setRevenueFix({ open: false, scanning: false, applying: false, result: null })}
        scanning={revenueFix.scanning}
        applying={revenueFix.applying}
        result={revenueFix.result}
        onApply={handleApplyRevenueFix}
      />

      <ExportCsvModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        columns={orderedColumns}
        rowCount={sortedOrders.length}
        onExport={handleExportCsvFiltered}
      />

      <ShipCompleteDialog
        open={shipPrompt.open}
        order={shipPrompt.order}
        carrierOptions={carrierOptions}
        defaultCarrier={preferredCarrier || carrierOptions[0] || 'POSTE - COL.CHRONO.'}
        remembered={preferredCarrier}
        onConfirm={handleFillShipping}
        onCancel={() => setShipPrompt({ open: false, order: null })}
      />

      <BulkEditDialog
        open={bulkEditOpen}
        onClose={() => setBulkEditOpen(false)}
        count={selectedRowIds.size}
        columns={orderedColumns}
        orders={orders}
        getColumnType={getColumnType}
        getColumnDropdownOptions={getColumnDropdownOptions}
        getColumnStatusConfig={getColumnStatusConfig}
        onApply={handleBulkApply}
      />

      {/* Context Menu for Column */}
      {contextMenu.show && (
        <div className="fixed inset-0 z-50" onClick={closeContextMenu}>
          <div className="absolute bg-white rounded-lg shadow-xl border border-zinc-200 py-1 min-w-[180px]" style={{ left: contextMenu.x, top: contextMenu.y }} onClick={(e) => e.stopPropagation()}>
            <button onClick={() => openColumnTypeModal(contextMenu.columnName)} className="w-full px-4 py-2 text-left text-sm hover:bg-zinc-100 flex items-center gap-2"><Settings2 className="w-4 h-4" />Modifier le type de colonne</button>
            <div className="border-t border-zinc-200 my-1" />
            <button onClick={() => handleDeleteColumn(contextMenu.columnName)} className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 text-red-600 flex items-center gap-2"><Trash2 className="w-4 h-4" />Supprimer la colonne</button>
          </div>
        </div>
      )}

      <ColumnTypeModal
        showColumnTypeModal={showColumnTypeModal} setShowColumnTypeModal={setShowColumnTypeModal}
        selectedColumnForType={selectedColumnForType}
        columnTypeForm={columnTypeForm} setColumnTypeForm={setColumnTypeForm}
        COLUMN_TYPES_WITH_ICONS={COLUMN_TYPES_WITH_ICONS}
        addDropdownOption={addDropdownOption} removeDropdownOption={removeDropdownOption}
        addAllExistingValues={addAllExistingValues} existingValues={existingValues}
        handleSaveColumnType={handleSaveColumnType} handleDeleteColumn={handleDeleteColumn}
        formulaInputRef={formulaInputRef} columns={columns} orders={orders}
      />

      <StatusFilterDropdown
        showStatusFilter={showStatusFilter} setShowStatusFilter={setShowStatusFilter}
        statusFilterPosition={statusFilterPosition}
        availableStatuses={availableStatuses} statusFilter={statusFilter}
        toggleStatusFilter={toggleStatusFilter}
        selectAllStatuses={selectAllStatuses} clearStatusFilter={clearStatusFilter}
      />

      <StatusFilterDropdown
        showStatusFilter={showTypeFilter} setShowStatusFilter={setShowTypeFilter}
        statusFilterPosition={typeFilterPosition}
        availableStatuses={availableTypes} statusFilter={typeFilter}
        toggleStatusFilter={toggleTypeFilter}
        selectAllStatuses={selectAllTypes} clearStatusFilter={clearTypeFilter}
        label="Filtrer par type"
        dropdownClass="type-filter-dropdown"
      />

      {/* Date Picker Dropdown */}
      <InlineDatePicker
        isOpen={showDatePicker}
        position={datePickerPosition}
        context={datePickerContext}
        onClose={() => { setShowDatePicker(false); setDatePickerContext({ orderId: null, columnName: null, currentValue: null }); }}
        onSelect={handleDateSelect}
        onClear={handleClearDate}
      />

      {/* Dropdown Picker Popup */}
      <InlineDropdownPicker
        isOpen={showDropdownPicker}
        position={dropdownPickerPosition}
        context={dropdownPickerContext}
        options={dropdownOptions}
        onClose={() => { setShowDropdownPicker(false); setDropdownPickerContext({ orderId: null, columnName: null, currentValue: null }); setCustomDropdownValue(""); }}
        onSelect={(orderId, columnName, value) => handleDropdownSelect(value)}
        onClear={() => handleClearDropdownValue()}
        onAddOption={(orderId, columnName, value) => { if (!dropdownOptions.includes(value)) setDropdownOptions(prev => [...prev, value].sort()); handleDropdownSelect(value); }}
        onDeleteOption={handleDeleteDropdownOption}
      />

      {/* Comments Modal */}
      <CommentsPanel
        isOpen={commentsModal.open}
        onClose={closeCommentsModal}
        orderId={commentsModal.orderId}
        orderName={commentsModal.orderName}
        currentUserEmail={user?.email}
        isAdmin={user?.email === "p.gilibert@storespergolas.com" || user?.is_admin}
        onNavigateToOrder={(orderId) => {
          navigateToRow(orderId);
        }}
        onNotificationUpdate={() => setNotifRefreshTrigger(prev => prev + 1)}
      />

      <AttachmentsModal
        attachmentModal={attachmentModal} closeAttachmentModal={closeAttachmentModal}
        handleFileUpload={handleFileUpload} isUploading={isUploading}
        attachments={attachments} previewAttachment={previewAttachment}
        downloadAttachment={downloadAttachment} handleDeleteAttachment={handleDeleteAttachment}
        handleTogglePrintWithSheet={handleTogglePrintWithSheet}
      />

      <EmailsModal
        emailModal={emailModal} closeEmailModal={closeEmailModal}
        emailForm={emailForm} handleEmailContentChange={handleEmailContentChange}
        detectedAttachments={detectedAttachments} emailAttachments={emailAttachments}
        removeEmailAttachment={removeEmailAttachment}
        handleEmailAttachmentSelect={handleEmailAttachmentSelect}
        handleAddEmail={handleAddEmail} isUploading={isUploading} emails={emails}
      />

      <PreviewLightbox previewModal={previewModal} closePreviewModal={closePreviewModal} />

      <AddColumnModal
        showAddColumnModal={showAddColumnModal} setShowAddColumnModal={setShowAddColumnModal}
        newColumnForm={newColumnForm} setNewColumnForm={setNewColumnForm}
        COLUMN_TYPES_WITH_ICONS={COLUMN_TYPES_WITH_ICONS}
        handleCreateColumn={handleCreateColumn} isCreatingColumn={isCreatingColumn}
      />

      {/* Row Context Menu */}
      {rowContextMenu.show && (
        <div className="fixed inset-0 z-50" onClick={() => setRowContextMenu({ show: false, x: 0, y: 0, orderId: null, orderName: "" })}>
          <div className="absolute bg-white rounded-lg shadow-xl border border-zinc-200 py-1 min-w-[200px]" style={{ left: rowContextMenu.x, top: rowContextMenu.y }} onClick={(e) => e.stopPropagation()}>
            <button onClick={() => openEnrichModal(rowContextMenu.orderId, rowContextMenu.orderName)} className="w-full px-4 py-2 text-left text-sm hover:bg-orange-50 text-orange-700 flex items-center gap-2" data-testid="ctx-enrich-btn">
              <RefreshCcw className="w-4 h-4" />Enrichir avec un email
            </button>
            <button onClick={() => { openAttachmentModal(rowContextMenu.orderId, rowContextMenu.orderName); setRowContextMenu({ show: false, x: 0, y: 0, orderId: null, orderName: "" }); }} className="w-full px-4 py-2 text-left text-sm hover:bg-zinc-100 flex items-center gap-2">
              <Paperclip className="w-4 h-4" />Pièces jointes
            </button>
            <button onClick={() => { openEmailModal(rowContextMenu.orderId, rowContextMenu.orderName); setRowContextMenu({ show: false, x: 0, y: 0, orderId: null, orderName: "" }); }} className="w-full px-4 py-2 text-left text-sm hover:bg-zinc-100 flex items-center gap-2">
              <Mail className="w-4 h-4" />Emails
            </button>
            <button onClick={() => { openCommentsModal(rowContextMenu.orderId, rowContextMenu.orderName); setRowContextMenu({ show: false, x: 0, y: 0, orderId: null, orderName: "" }); }} className="w-full px-4 py-2 text-left text-sm hover:bg-zinc-100 flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />Commentaires
            </button>
            <div className="border-t border-zinc-200 my-1" />
            <button onClick={() => { handleDeleteRow(rowContextMenu.orderId); setRowContextMenu({ show: false, x: 0, y: 0, orderId: null, orderName: "" }); }} className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 text-red-600 flex items-center gap-2">
              <Trash2 className="w-4 h-4" />Supprimer la ligne
            </button>
          </div>
        </div>
      )}

      {/* Enrich from Tableau */}
      <EnrichOrderModal
        isOpen={enrichModal.open}
        onClose={() => setEnrichModal({ open: false, orderId: null, orderName: "" })}
        orderId={enrichModal.orderId}
        orderName={enrichModal.orderName}
        onExtracted={handleEnrichExtracted}
      />

      {/* Extraction Validation for enrichment */}
      <ExtractionValidationModal
        isOpen={validationModal.open}
        onClose={() => setValidationModal({ open: false, mode: "enrichissement", extractedData: {}, existingData: {}, orderId: null })}
        mode={validationModal.mode}
        extractedData={validationModal.extractedData}
        existingData={validationModal.existingData}
        orderId={validationModal.orderId}
        onSave={handleValidationSave}
        isLoading={isSavingValidation}
      />

      <BackupManagerModal open={showBackupManager} onClose={() => setShowBackupManager(false)} />
    </div>
  );
}
